/*
  assets/littleg-place-mission-bridge.js — PLACE-MISSION-BRIDGE-03

  Canonical owner:
  Place -> active Mission projection and round_key mission hydration on /pages/game.

  Family surface:
  Place -> Mission -> completion -> gem.
  The family UI does not expose Evidence, Observation, Signal, Place Record,
  GCityPartners review or GLG professional verification terminology.

  Reads:
  GET https://api.glhksolution.com/api/explore/place?place_id=...

  Writes:
  Anonymous structured observation to the same canonical Place resource after
  a valid LittleG mission-state completion.

  No child/customer identity or precise household trail is sent.

  Complements:
  littleg-opening-gate.js owns Place discovery, public-information summary,
  readiness / missing-state presentation and professional handoff.

  Boundary:
  - no Opening Gate result ownership
  - no Screen 01-08 routing ownership
  - no Shopify metaobject mutation
  - no GFun ledger ownership
  - no professional conclusion
  - no formal evidence claim
  - no Place Record consumer presentation
*/
(function littleGPlaceMissionBridge(window, document) {
  'use strict';

  var VERSION = 'PLACE-MISSION-BRIDGE-03';
  var ROOT_SELECTOR = '[data-littleg-game-app]';
  var API_SELECTOR = '[data-lg-explore-root]';

  var PUBLIC_API_BASE = 'https://api.glhksolution.com';
  var LEGACY_PUBLIC_API_ORIGIN = 'https://api.littleg.group';
  var PUBLIC_EXPLORE_API = PUBLIC_API_BASE + '/api/explore';
  var PUBLIC_EXPLORE_PLACE_API = PUBLIC_EXPLORE_API + '/place';

  if (window.__LITTLEG_PLACE_MISSION_BRIDGE__ === VERSION) return;
  window.__LITTLEG_PLACE_MISSION_BRIDGE__ = VERSION;

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }

  function currentUrl() {
    try {
      return new URL(window.location.href);
    } catch (_) {
      return null;
    }
  }

  function localeFamily(root) {
    var explicit = clean(
      root && (root.dataset.localeFamily || root.dataset.locale || root.getAttribute('lang'))
    );

    var locale = explicit || clean(document.documentElement.lang) || 'en';
    var normalized = locale.toLowerCase();

    if (normalized === 'zh-cn' || normalized.indexOf('zh-hans') === 0) {
      return 'zh-CN';
    }

    if (normalized === 'zh-tw' || normalized.indexOf('zh-hant-tw') === 0) {
      return 'zh-TW';
    }

    if (
      normalized === 'zh-hk' ||
      normalized === 'zh-mo' ||
      normalized.indexOf('zh-hant-hk') === 0
    ) {
      return 'zh-HK';
    }

    return 'en';
  }

  /*
    Mission-only consumer copy.

    Deliberately does NOT duplicate littleg-opening-gate.js ownership:
    - no Place Record heading
    - no evidence states
    - no public-source verification copy
    - no readiness copy
    - no professional handoff copy
  */
  function missionCopy(root) {
    var family = localeFamily(root);

    var copy = {
      en: {
        exploreTitle: 'Explore',
        exploreLead: 'Choose a place. See what you can do there today.',

        fallbackPlace: 'This place',
        fallbackMission: 'City mission',

        missionAvailableOne: '1 mission is ready here today.',
        missionAvailableMany: '{count} missions are ready here today.',
        missionEmpty: 'No missions are available here right now.',

        minutes: 'min',
        startMission: 'Start mission →',

        chooseAnother: 'Choose another place',
        chooseAnotherMeta: 'Explore more places nearby',
        viewPlaces: 'See places →',

        unavailable: 'We can’t load this place right now. Go back to Explore and try again.',
      },

      'zh-CN': {
        exploreTitle: '探索',
        exploreLead: '选一个地方，看看今天能做什么。',

        fallbackPlace: '这个地方',
        fallbackMission: '城市小任务',

        missionAvailableOne: '这里今天有 1 个任务。',
        missionAvailableMany: '这里今天有 {count} 个任务。',
        missionEmpty: '这里现在还没有可做的任务。',

        minutes: '分钟',
        startMission: '开始任务 →',

        chooseAnother: '选其他地方',
        chooseAnotherMeta: '继续探索附近地方',
        viewPlaces: '看看地方 →',

        unavailable: '暂时无法打开这个地方。返回探索再试一次。',
      },

      'zh-TW': {
        exploreTitle: '探索',
        exploreLead: '選一個地方，看看今天能做什麼。',

        fallbackPlace: '這個地方',
        fallbackMission: '城市小任務',

        missionAvailableOne: '這裡今天有 1 個任務。',
        missionAvailableMany: '這裡今天有 {count} 個任務。',
        missionEmpty: '這裡現在還沒有可做的任務。',

        minutes: '分鐘',
        startMission: '開始任務 →',

        chooseAnother: '選其他地方',
        chooseAnotherMeta: '繼續探索附近地方',
        viewPlaces: '看看地方 →',

        unavailable: '暫時無法開啟這個地方。返回探索再試一次。',
      },

      'zh-HK': {
        exploreTitle: '探索',
        exploreLead: '揀一個地方，睇吓今日有咩任務。',

        fallbackPlace: '呢個地方',
        fallbackMission: '城市小任務',

        missionAvailableOne: '呢度今日有 1 個任務。',
        missionAvailableMany: '呢度今日有 {count} 個任務。',
        missionEmpty: '呢度而家未有可做嘅任務。',

        minutes: '分鐘',
        startMission: '開始任務 →',

        chooseAnother: '揀其他地方',
        chooseAnotherMeta: '繼續探索附近地方',
        viewPlaces: '睇吓地方 →',

        unavailable: '暫時未能開啟呢個地方。返回探索再試一次。',
      },
    };

    return copy[family] || copy.en;
  }

  function countCopy(template, count) {
    return clean(template).replace('{count}', String(count));
  }

  function setText(root, selector, value) {
    var node = root.querySelector(selector);
    if (node) node.textContent = clean(value);
  }

  function canonicalExploreUrl(value, fallback) {
    var configured = clean(value);

    if (!configured || configured.indexOf('/apps/gcity-api/') === 0) {
      return fallback;
    }

    try {
      var parsed = new URL(configured, window.location.origin);

      if (parsed.origin === LEGACY_PUBLIC_API_ORIGIN) {
        return fallback;
      }

      if (
        parsed.origin === window.location.origin &&
        parsed.pathname.indexOf('/apps/gcity-api/') === 0
      ) {
        return fallback;
      }

      return parsed.toString();
    } catch (_) {
      return fallback;
    }
  }

  function normalizeExploreEndpoints(root) {
    var nodes = root.querySelectorAll('[data-lg-explore-root], [data-lg-explore-map]');

    Array.prototype.forEach.call(nodes, function (node) {
      node.dataset.lgExploreApi = canonicalExploreUrl(
        node.dataset.lgExploreApi,
        PUBLIC_EXPLORE_API
      );

      node.dataset.lgExplorePlaceApi = canonicalExploreUrl(
        node.dataset.lgExplorePlaceApi,
        PUBLIC_EXPLORE_PLACE_API
      );

      node.dataset.lgPublicApiBase = PUBLIC_API_BASE;
    });
  }

  function placeApi(root) {
    var exploreRoot = root.querySelector(API_SELECTOR);

    return canonicalExploreUrl(
      exploreRoot && exploreRoot.dataset.lgExplorePlaceApi,
      PUBLIC_EXPLORE_PLACE_API
    );
  }

  /*
    The canonical mission source is Place.active_missions.

    Legacy mission entry UI is hidden so the family cannot enter a parallel
    mission source that bypasses Place.
  */
  function hideLegacyMissionEntry(root) {
    var lobby = root.querySelector('[data-lg-explore-root]');
    if (!lobby) return;

    lobby.dataset.missionSourceState = 'place-first';

    var primary = lobby.querySelector('.lg-explore-primary');
    if (primary) primary.hidden = true;

    var legacyMissionCard = lobby.querySelector('.lg-explore-overview-card.is-mission');

    if (legacyMissionCard) legacyMissionCard.hidden = true;
  }

  function showExplorePlaces(root) {
    var copy = missionCopy(root);

    hideLegacyMissionEntry(root);

    root.dataset.placeMissionMode = 'explore-places';

    var lobby = root.querySelector('[data-lg-explore-root]');
    if (!lobby) return;

    setText(lobby, '#lg-screen-01-title', copy.exploreTitle);

    var lead = lobby.querySelector('.lg-explore-hero__lead');
    if (lead) lead.textContent = copy.exploreLead;
  }

  function missionHref(placeId, roundKey) {
    var url = currentUrl() || new URL('/pages/game', window.location.origin);

    url.search = '';
    url.hash = '';

    url.searchParams.set('view', 'game');
    url.searchParams.set('screen', 'brief');
    url.searchParams.set('place_id', placeId);
    url.searchParams.set('round_key', roundKey);

    return url.pathname + url.search;
  }

  function exploreHref() {
    var url = currentUrl() || new URL('/pages/game', window.location.origin);

    url.search = '';
    url.hash = '';

    url.searchParams.set('view', 'game');
    url.searchParams.set('screen', 'lobby');
    url.searchParams.set('category', 'all');

    return url.pathname + url.search;
  }

  function missionCard(placeId, mission, copy) {
    var link = document.createElement('a');

    link.className = 'lg-explore-overview-card is-mission';
    link.href = missionHref(placeId, mission.round_key);

    link.setAttribute('data-place-mission-round-key', clean(mission.round_key));

    var icon = document.createElement('span');
    icon.className = 'lg-explore-overview-card__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '💎';

    var content = document.createElement('span');
    content.className = 'lg-explore-overview-card__copy';

    var title = document.createElement('strong');
    title.textContent = clean(mission.title) || copy.fallbackMission;

    var meta = document.createElement('small');

    meta.textContent =
      (Number(mission.estimated_minutes) || 3) +
      ' ' +
      copy.minutes +
      ' · 💎 +' +
      (Number(mission.gem_reward) || 1);

    var action = document.createElement('span');
    action.textContent = copy.startMission;

    content.append(title, meta, action);
    link.append(icon, content);

    return link;
  }

  function renderMissionList(root, place, missions) {
    var copy = missionCopy(root);

    var lobby = root.querySelector('[data-lg-explore-root]');
    if (!lobby) return;

    hideLegacyMissionEntry(root);

    root.dataset.placeMissionMode = 'mission-list';
    root.dataset.placeId = clean(place && place.id);

    setText(lobby, '#lg-screen-01-title', clean(place && place.display_name) || copy.fallbackPlace);

    var lead = lobby.querySelector('.lg-explore-hero__lead');

    if (lead) {
      if (missions.length === 1) {
        lead.textContent = copy.missionAvailableOne;
      } else if (missions.length > 1) {
        lead.textContent = countCopy(copy.missionAvailableMany, missions.length);
      } else {
        lead.textContent = copy.missionEmpty;
      }
    }

    var overview = lobby.querySelector('.lg-explore-overview');
    if (!overview) return;

    Array.prototype.slice.call(overview.children).forEach(function (child) {
      child.hidden = true;
    });

    var oldHost = overview.querySelector('[data-lg-place-mission-list]');

    if (oldHost) oldHost.remove();

    /*
      Keep one generated host owner.

      Previous implementation created only a DocumentFragment while also
      querying [data-lg-place-mission-list], so there was no actual host
      carrying that selector to remove on a later render.
    */
    var host = document.createElement('div');
    host.setAttribute('data-lg-place-mission-list', '');
    host.className = 'lg-place-mission-list';

    missions.forEach(function (mission) {
      host.appendChild(missionCard(place.id, mission, copy));
    });

    var back = document.createElement('a');
    back.className = 'lg-explore-overview-card is-nearby';
    back.href = exploreHref();

    const backIcon = document.createElement('span');
    backIcon.className = 'lg-explore-overview-card__icon';
    backIcon.setAttribute('aria-hidden', 'true');

    const backIconTemplate = document.querySelector(
      'template[data-lg-place-mission-icon="map-pin"]'
    );
    const backIconSource =
      backIconTemplate && backIconTemplate.content
        ? backIconTemplate.content.querySelector('svg')
        : null;

    if (backIconSource) {
      backIcon.appendChild(backIconSource.cloneNode(true));
    }

    var backCopy = document.createElement('span');
    backCopy.className = 'lg-explore-overview-card__copy';

    var backTitle = document.createElement('strong');
    backTitle.textContent = copy.chooseAnother;

    var backMeta = document.createElement('small');
    backMeta.textContent = copy.chooseAnotherMeta;

    var backAction = document.createElement('span');
    backAction.textContent = copy.viewPlaces;

    backCopy.append(backTitle, backMeta, backAction);
    back.append(backIcon, backCopy);

    host.appendChild(back);
    overview.appendChild(host);
  }

  /*
    Mission hydration owns gameplay projection only.

    It does NOT turn mission fields into consumer-facing Place Record,
    evidence, professional or readiness terminology.
  */
  function hydrateMission(root, place, mission) {
    if (!mission) return false;

    var copy = missionCopy(root);

    var clues = Array.isArray(mission.clues) ? mission.clues.slice(0, 3) : [];

    root.dataset.placeId = clean(place && place.id);

    root.dataset.missionRoundKey = clean(mission.round_key);
    root.dataset.missionHandle = clean(mission.round_key);
    root.dataset.missionName = clean(mission.title);
    root.dataset.missionDescription = clean(mission.description);

    root.dataset.missionClue1 = clean(clues[0]);
    root.dataset.missionClue2 = clean(clues[1]);
    root.dataset.missionClue3 = clean(clues[2]);

    root.dataset.missionDuration = String(Number(mission.estimated_minutes) || 3);

    root.dataset.missionGameplayMode = clean(mission.interaction_type);

    root.dataset.serviceLane = clean(mission.evidence_family);

    root.dataset.observationType = clean(mission.observation_type);

    root.dataset.evidenceFamily = clean(mission.evidence_family);

    root.dataset.gemReward = String(Number(mission.gem_reward) || 1);

    root.dataset.lgHints = JSON.stringify(clues);
    root.dataset.placeMissionMode = 'selected';

    root.__placeMissionDefinition = mission;
    root.__placeMissionPlace = place;

    setText(root, '[data-lg-lobby-title]', mission.title || copy.fallbackMission);

    setText(root, '[data-lg-mission-lead]', mission.description);

    setText(
      root,
      '[data-lg-mission-duration]',
      (Number(mission.estimated_minutes) || 3) + ' ' + copy.minutes
    );

    /*
      Runtime interoperability event.

      This exposes the selected Place + Mission contract to other runtime
      consumers without transferring UI ownership to them.
    */
    try {
      document.dispatchEvent(
        new CustomEvent('littleg:place-mission-ready', {
          detail: {
            place: place,
            mission: mission,
          },
        })
      );
    } catch (_) {}

    return true;
  }

  async function loadPlaceMission(root) {
    var copy = missionCopy(root);

    var url = currentUrl();

    var placeId = url && clean(url.searchParams.get('place_id'));

    var roundKey = url && clean(url.searchParams.get('round_key'));

    if (!placeId) {
      showExplorePlaces(root);
      return;
    }

    root.dataset.placeId = placeId;

    if (roundKey) {
      root.dataset.missionRoundKey = roundKey;
    }

    root.hidden = true;
    root.setAttribute('aria-busy', 'true');

    try {
      var endpoint = new URL(placeApi(root));
      endpoint.searchParams.set('place_id', placeId);

      var response = await fetch(endpoint.toString(), {
        credentials: 'omit',
        headers: {
          Accept: 'application/json',
          'X-Request-ID': 'place-mission-' + Date.now(),
        },
      });

      var payload = await response.json();

      if (!response.ok || payload.success === false) {
        throw new Error('place_unavailable');
      }

      var data = payload && payload.data && typeof payload.data === 'object' ? payload.data : {};

      var place = data.place || data.item || null;

      var missions = Array.isArray(data.active_missions) ? data.active_missions : [];

      root.__placeMissions = missions;
      root.__placeMissionPlace = place;

      if (roundKey) {
        var selected = missions.find(function (mission) {
          return clean(mission.round_key) === roundKey;
        });

        if (!selected || !hydrateMission(root, place, selected)) {
          root.dataset.placeMissionMode = 'mission-not-active';

          var next = currentUrl();

          if (next) {
            next.searchParams.set('view', 'game');
            next.searchParams.set('screen', 'lobby');
            next.searchParams.delete('round_key');

            window.history.replaceState(null, '', next.pathname + next.search + next.hash);
          }

          renderMissionList(root, place, missions);
        }
      } else {
        renderMissionList(root, place, missions);
      }
    } catch (_) {
      root.dataset.placeMissionMode = 'unavailable';

      var toast = root.querySelector('[data-lg-toast]');

      if (toast) {
        toast.textContent = copy.unavailable;
        toast.hidden = false;
      }
    } finally {
      root.hidden = false;
      root.removeAttribute('aria-busy');
    }
  }

  function observationIdempotency(detail, placeId, roundKey) {
    var base =
      clean(detail && detail.result_id) ||
      [placeId, roundKey, clean(detail && detail.completed_at)].join(':');

    return ('obs-' + base).replace(/[^A-Za-z0-9._:-]/g, '-').slice(0, 120);
  }

  /*
    Internal structured observation only.

    The family UI still sees:
      Mission complete -> gem

    The platform receives:
      Mission complete -> structured observation -> same Place

    Do not expose these field names as family-facing copy.
  */
  function observationPayload(detail) {
    return {
      clues: Array.isArray(detail && detail.clues) ? detail.clues.slice(0, 3) : [],

      guess: detail && detail.guess && typeof detail.guess === 'object' ? detail.guess : {},

      completion_source: clean(detail && detail.completion_source) || 'mission-complete',
    };
  }

  async function submitObservation(root, detail) {
    var url = currentUrl();

    var placeId = clean(root.dataset.placeId || (url && url.searchParams.get('place_id')));

    var roundKey = clean(
      root.dataset.missionRoundKey || (url && url.searchParams.get('round_key'))
    );

    if (!placeId || !roundKey || !detail || detail.three_clues_complete !== true) {
      return;
    }

    var key = observationIdempotency(detail, placeId, roundKey);

    var sessionKey = 'littleg_place_observation_' + key;

    try {
      if (window.sessionStorage.getItem(sessionKey) === 'sent') {
        return;
      }
    } catch (_) {}

    try {
      var endpoint = new URL(placeApi(root));
      endpoint.searchParams.set('place_id', placeId);

      var response = await fetch(endpoint.toString(), {
        method: 'POST',
        credentials: 'omit',

        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Request-ID': 'place-observation-' + Date.now(),
          'X-Idempotency-Key': key,
        },

        body: JSON.stringify({
          place_id: placeId,
          round_key: roundKey,
          observation_value: observationPayload(detail),
        }),
      });

      var payload = await response.json().catch(function () {
        return {};
      });

      if (!response.ok || payload.success === false) {
        throw new Error('observation_rejected');
      }

      var state = clean(payload && payload.data && payload.data.state) || 'created';

      root.dataset.placeObservationStatus = state;

      try {
        window.sessionStorage.setItem(sessionKey, 'sent');
      } catch (_) {}

      /*
        Internal bridge event.

        Important:
        this event does not render "Place Record updated" to the family.

        A future GCityPartners / Place consumer may listen for this state,
        while Opening Gate remains the owner of Place Record presentation.
      */
      try {
        document.dispatchEvent(
          new CustomEvent('littleg:place-observation-recorded', {
            detail: {
              place_id: placeId,
              round_key: roundKey,
              state: state,
            },
          })
        );
      } catch (_) {}
    } catch (_) {
      root.dataset.placeObservationStatus = 'deferred';

      /*
        Observation failure must not break the child's completed Mission
        or gem UX. The write is a platform-side concern.
      */
      try {
        document.dispatchEvent(
          new CustomEvent('littleg:place-observation-deferred', {
            detail: {
              place_id: placeId,
              round_key: roundKey,
            },
          })
        );
      } catch (_) {}
    }
  }

  function boot() {
    var root = document.querySelector(ROOT_SELECTOR);

    if (!root || root.dataset.placeMissionBridgeReady === VERSION) {
      return;
    }

    root.dataset.placeMissionBridgeReady = VERSION;

    normalizeExploreEndpoints(root);

    var url = currentUrl();

    if (url && url.searchParams.get('place_id')) {
      root.dataset.placeId = clean(url.searchParams.get('place_id'));

      if (url.searchParams.get('round_key')) {
        root.dataset.missionRoundKey = clean(url.searchParams.get('round_key'));
      }
    }

    loadPlaceMission(root);
  }

  /*
    Mission runtime is the completion authority.

    Bridge only consumes the canonical completion event and writes the
    anonymous Place observation. It does not award gems or own mission-state.
  */
  window.addEventListener('littleg:mission-state', function (event) {
    var root = document.querySelector(ROOT_SELECTOR);

    if (root) {
      submitObservation(root, (event && event.detail) || {});
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})(window, document);
